import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Form, Modal, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { activateVehicleIntegration } from '../../../../queries/getVehicles.ts';
import { VehicleIntegrationShift } from '../../../../schema/VehicleIntegration.ts';
import { useIntegrationsByVehicle } from '../../../../services/Vehicle.ts';
import { formatShiftDates } from '../../../../utils/fleetUtils.ts';
import { LoadingButton } from '../../../ui/Button.tsx';

interface Props {
  show: boolean;
  onHide: () => void;
  vehicleId: number;
}

export function ChangeShiftModal({ show, onHide, vehicleId }: Readonly<Props>) {
  const { t } = useTranslation();
  const params = useParams() as { territorySlug: string };
  const queryClient = useQueryClient();
  const { data: integrations } = useIntegrationsByVehicle(params.territorySlug, vehicleId);
  const [activeByRow, setActiveByRow] = useState<Record<number, boolean>>({});

  const activateIntegrationMutation = useMutation({
    mutationFn: (integrationId: number) =>
      activateVehicleIntegration(params.territorySlug, vehicleId, { integration_id: integrationId }),
    onSuccess: () => {
      toast.success(t('vehicle-shift.activate-integration-success'));
      queryClient.invalidateQueries({ queryKey: ['integrations', vehicleId] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', params.territorySlug] });
      queryClient.invalidateQueries({ queryKey: ['shift', vehicleId] });
      queryClient.invalidateQueries({ queryKey: ['roadmap', params.territorySlug, vehicleId] });
      onHide();
    },
    meta: { errorToastKey: 'vehicle-shift.activate-integration-error' },
  });

  useEffect(() => {
    if (!integrations) {
      setActiveByRow({});
      return;
    }

    const initialState = integrations.reduce(
      (acc: Record<number, boolean>, item: VehicleIntegrationShift) => {
        acc[item.integration.id] = Boolean(item.integration.is_active);
        return acc;
      },
      {},
    );

    setActiveByRow(initialState);
  }, [integrations]);

  const handleActiveChange = (rowIndex: number, checked: boolean) => {
    setActiveByRow(prev => {
      if (!checked) {
        return {
          ...prev,
          [rowIndex]: false,
        };
      }

      const next: Record<number, boolean> = {};
      Object.keys(prev).forEach(key => {
        next[Number(key)] = false;
      });
      next[rowIndex] = true;

      return next;
    });
  };

  const handleSave = () => {
    const selectedIntegration = integrations?.find((item: VehicleIntegrationShift) =>
      Boolean(activeByRow[item.integration.id]),
    );

    if (!selectedIntegration) {
      toast.error(t('vehicle-shift.select-one-active-integration'));
      return;
    }

    activateIntegrationMutation.mutate(selectedIntegration.integration.id);
  };
  const hasSelectedDifferentIntegration =
    integrations?.some((item: VehicleIntegrationShift) => {
      const isSelected = Boolean(activeByRow[item.integration.id]);
      const isCurrentlyActive = item.integration.is_active;
      const match = isSelected && !isCurrentlyActive;

      return match;
    }) ?? false;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className={'border-0'}>
        <Modal.Title>
          <p className="card-subtitle">{t('vehicle-shift.change-dispatch-provider')}</p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="justify-content-between">
        <div className="rounded-3 overflow-hidden border">
          <Table striped hover className="mb-0">
            <thead className="text-bold">
              <tr>
                <th>{t('vehicle-shift.active')}</th>
                <th>{t('vehicle-shift.dispatch-provider-name')}</th>
                <th>{t('vehicle-shift.dispatch-provider-identifier')}</th>
                <th>{t('vehicle-shift.shift-id')}</th>
                <th>{t('vehicle-shift.shift-information')}</th>
              </tr>
            </thead>
            <tbody>
              {integrations?.map((item: VehicleIntegrationShift) => (
                <tr key={item.integration.id} className="text-basic">
                  <td>
                    <Form.Check
                      type="checkbox"
                      name={`active-${item.integration.id ?? item.integration.id}`}
                      checked={Boolean(activeByRow[item.integration.id])}
                      onChange={event => handleActiveChange(item.integration.id, event.target.checked)}
                    />
                  </td>
                  <td>{item.integration.dispatch_provider_name}</td>
                  <td>{item.integration.dispatch_provider_identifier}</td>
                  <td>{item.shift ? item.shift.id : t('vehicle-shift.not-available')}</td>
                  <td>{item.shift ? formatShiftDates(item.shift) : t('vehicle-shift.no-shift')}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0">
        <LoadingButton
          type={'submit-button'}
          text={t('vehicle-shift.save-button')}
          imagePath={null}
          loading={activateIntegrationMutation.isPending}
          onClick={handleSave}
          disabled={!hasSelectedDifferentIntegration}
        />
      </Modal.Footer>
    </Modal>
  );
}
